<script setup lang="ts">
import useVuelidate from '@vuelidate/core';
import { useDropZone, useFileDialog } from '@vueuse/core';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';
import { helpers } from '@vuelidate/validators';
import { uploadFile } from '../../../dataProviders/files';
import { useOrderStore } from '../../../stores/orderForm';
import type { FileInfo } from '../../../types/File';
import type { NewOrder } from '../../../types/Order';

const { t } = useI18n();
const toast = useToast();
const { order, isPreviewMode } = useOrderStore();

const abortController = new AbortController();
onUnmounted(() => {
  abortController.abort();
});

const pendingFiles = ref<Map<string, { file: File, meta: { progress: number, uploading: boolean, uploaded: boolean } }>>(new Map());

function validFileIds() {
  if (isPreviewMode || ('files' in order))
    return true;
  return pendingFiles.value.size > 0;
}
const rules = computed(() => ({
  pendingFiles: { required: helpers.withMessage(t('errors.orderFiles'), validFileIds) },
}));

const v$ = useVuelidate(rules, { pendingFiles });

const { open, reset, onChange: onFilesChange } = useFileDialog({
  accept: '.stl, .ply, .dcm',
  multiple: true,
});

const dropZoneRef = ref<HTMLDivElement>();
const { isOverDropZone } = useDropZone(dropZoneRef, onDrop);

function onDrop(files: File[] | null) {
  files?.forEach(file => handleSelectedFile(file));
}

onFilesChange((event) => {
  if (event === null)
    return;

  v$.value.$reset();
  for (let index = 0; index < event.length; index++) {
    const file = event.item(index);
    handleSelectedFile(file);
  }
});

function handleSelectedFile(file: File | null) {
  if (!file)
    return;
  if (!file.name.includes('.stl') && !file.name.includes('.ply') && !file.name.includes('.dcm')) {
    toast.add({ severity: 'error', summary: t('orderForm.file.errors.title'), detail: t('orderForm.file.errors.type'), life: 5000 });
    console.error(t('orderForm.file.errors.title'), file.name, file);
    return;
  }
  pendingFiles.value.set(file.name, { file, meta: { progress: 0, uploaded: false, uploading: false } });
}

const isUploading = ref(false);
async function uploadFiles() {
  isUploading.value = true;
  const promises: Promise<FileInfo | null>[] = [];
  pendingFiles.value.forEach((entry) => {
    entry.meta.uploading = true;
    promises.push(uploadFile(entry.file, abortController, (e) => {
      entry.meta.progress = e;
      if (e >= 100) {
        entry.meta.uploaded = true;
        entry.meta.uploading = false;
      }
    }));
  });
  const resp = await Promise.allSettled(promises);

  (order as NewOrder).file_ids = [];
  resp.forEach((res) => {
    if (res.status === 'fulfilled' && res.value) {
      (order as NewOrder).file_ids.push(res.value.id);
    }
  });
  isUploading.value = false;
}

function removeFile(fileName: string) {
  pendingFiles.value.delete(fileName);
  reset();
}

defineExpose({ pendingFiles: pendingFiles.value, uploadFiles });
</script>

<template>
  <!-- ID is used for scroll animation -->
  <div class="relative">
    <img
      v-if="order.machine_type === 'milling'"
      src="/robot-wave-second.svg"
      alt="robot"
      class="absolute right-4 top-1 h-15 transform -translate-y-1/2"
    >
    <img
      v-if="order.machine_type === '3d' || order.machine_type === 'models'"
      src="/robot-wave.svg"
      alt="robot"
      class="absolute right-4 top-2 h-15 transform -translate-y-1/2"
    >
    <Fieldset
      id="filesfieldset"
      class="mb-6"
      :pt="{ legend: 'bg-gray-200 border border-gray-300 hover:bg-gray-300', root: 'mt-7' }"
    >
      <template #legend>
        <div class="align-items-center flex gap-2 px-2">
          <span class="font-bold">Файлове</span>
          <i class="pi pi-file-edit !leading-[unset]" />
        </div>
      </template>
      <div>
        <FormItem
          field="pendingFiles"
          :label="$t('orderForm.file.label')"
          :v$="v$"
          required
        >
          <tempalte v-if="!isPreviewMode && 'file_ids' in order">
            <div
              ref="dropZoneRef"
              class="h-23 flex flex-col cursor-pointer items-center justify-center border-2 border-primary rounded-2 border-dashed bg-transparent p-2 text-primary font-bold transition-all"
              :class="{
                'mb-6': pendingFiles.size || order.file_ids?.length,
                '!bg-gray-100': isOverDropZone,
              }"
              @click="() => open()"
            >
              <p> <i :class="[isOverDropZone ? 'pi-upload' : 'pi-file-import']" class="pi mr-2 transition-all" /> Плъзнете и пуснете файловете тук</p>
              <p>или кликнете, за да изберете файлове</p>
            </div>
          </tempalte>

          <div class="grid grid-cols-1 items-center gap-4 md:grid-cols-2">
            <FileItem
              v-for="entry of pendingFiles.values()"
              :key="entry.file.name + entry.file.size"
              :pending-file="entry.file"
              :upload-meta="entry.meta"
              @remove="removeFile(entry.file.name)"
            />
            <template v-if="'files' in order">
              <FileItem
                v-for="file of order.files"
                :key="file.name + file.size"
                :uploaded-file="file"
                :order="order"
              />
            </template>
          </div>
        </FormItem>
        <p class="mt-6 text-sm text-gray italic">
          <i class="pi pi-info-circle mr-1 align-top" /> За да изберете повече от един .stl, .ply и/или .dcm файл можете да задържите
          бутона
          Ctrl и да кликнете на всички желани файлове. За MacOS използвайте бутона command.
        </p>
      </div>
    </Fieldset>
  </div>
</template>
